import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFinishRevisionTranslation } from '@/features/exam-revision/hooks/useFinishRevisionTranslation';
import { useRevisionDetail } from '@/features/exam-revision/hooks/useRevisionDetail';
import { useRevisionLangs } from '@/features/exam-revision/hooks/useRevisionLangs';
import { useRevisionTranslation } from '@/features/exam-revision/hooks/useRevisionTranslation';
import { useSaveRevisionTranslationQuestion } from '@/features/exam-revision/hooks/useSaveRevisionTranslationQuestion';
import type { RevisionLang } from '@/features/exam-revision/types/revision';
import { ConfirmDialog } from '@/features/exam-revision/ui/organisms/ConfirmDialog';
import { RevisionTranslationTemplate } from '@/features/exam-revision/ui/templates/RevisionTranslationTemplate';
import { revisionCultureDisplay } from '@/features/exam-revision/utils/revisionLangMapper';

export const Route = createFileRoute(
  '/_authenticated/exam/revision/$examId/languages/$langId/translate/',
)({
  component: RevisionTranslationPage,
});

export function RevisionTranslationPage() {
  const { examId, langId } = Route.useParams();
  const botExamId = Number(examId);
  const botExamLangId = Number(langId);
  const navigate = useNavigate();
  const { t } = useTranslation('examRevision');

  const exam = useRevisionDetail(botExamId);
  const { langs } = useRevisionLangs(botExamId);

  const {
    current,
    currentIndex,
    total,
    examTotal,
    typeFilter,
    availableFilters,
    setTypeFilter,
    goTo,
    updateTranslation,
    buildSavePayload,
    title,
    targetLanguageInfo,
    isLoading,
    isError,
    refetch,
  } = useRevisionTranslation(botExamLangId);

  const { finishTranslation, isLoading: isFinishing } = useFinishRevisionTranslation(botExamId);
  const { saveQuestion, isSaving } = useSaveRevisionTranslationQuestion(botExamLangId);

  const handleSave = () => {
    const payload = buildSavePayload();
    if (payload) saveQuestion(payload);
  };

  const [confirmFinish, setConfirmFinish] = useState(false);

  // Idioma do cabeçalho: prioriza o grid real; recai nos dados da tradução.
  const lang: RevisionLang = langs.find(l => l.id === botExamLangId) ?? {
    id: botExamLangId,
    cultureInfo: targetLanguageInfo ?? '',
    cultureInfoDisplay: targetLanguageInfo
      ? revisionCultureDisplay(targetLanguageInfo)
      : (title ?? String(botExamLangId)),
    title: title ?? '',
    instruction: '',
    statusId: 1,
    statusText: '',
    questionCount: total,
    active: true,
  };

  const handleConfirmFinish = () => {
    finishTranslation(botExamLangId, {
      onSuccess: () => {
        setConfirmFinish(false);
        void navigate({
          to: '/exam/revision/$examId/languages' as never,
          params: { examId } as never,
        });
      },
    });
  };

  return (
    <>
      <RevisionTranslationTemplate
        exam={exam}
        lang={lang}
        current={current}
        currentIndex={currentIndex}
        total={total}
        examTotal={examTotal}
        typeFilter={typeFilter}
        availableFilters={availableFilters}
        onTypeFilter={setTypeFilter}
        onNavigate={goTo}
        onUpdate={updateTranslation}
        onSave={handleSave}
        onFinalize={() => setConfirmFinish(true)}
        isSaving={isSaving}
        isFinishing={isFinishing}
        isLoading={isLoading}
        isError={isError}
        onRetry={refetch}
      />

      <ConfirmDialog
        open={confirmFinish}
        title={t('translation.finishModal.title')}
        body={t('translation.finishModal.body')}
        confirmLabel={t('translation.finishModal.confirm')}
        cancelLabel={t('translation.finishModal.cancel')}
        tone="brand"
        isLoading={isFinishing}
        onClose={() => setConfirmFinish(false)}
        onConfirm={handleConfirmFinish}
      />
    </>
  );
}
